"use client";

import { useEffect } from "react";

import { readCookie } from "@/utils/cookies";
import { segmentEvent } from "@/utils/segmentfunctions";

/* Segment loads afterInteractive; retry for a while instead of dropping the event. */
const SEGMENT_POLL_MS = 500;
const SEGMENT_MAX_POLLS = 20;

/* "experimentId|path" entries already reported this session, for `oncePerSession`. */
const FIRED_KEY = "fme_experiment_viewed_ids";

/* One entry per experiment per page, so a test shared by several pages reports on each of them. */
function firedId(experimentId: string): string {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return `${experimentId}|${path}`;
}

function readFired(): string[] {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(FIRED_KEY) ?? "[]");
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

function markFired(id: string): void {
  try {
    sessionStorage.setItem(FIRED_KEY, JSON.stringify([...readFired(), id]));
  } catch {
    /* ignore quota / private mode */
  }
}

interface ExperimentTrackerProps {
  experimentId: string;
  experimentName: string;
  variantName: string;
  /* Fire only when this cookie holds `expectedValue`. Omit both to always fire (e.g. server-side VWO). */
  cookieName?: string;
  expectedValue?: string;
  /* Report each experiment once per page per session instead of on every mount (server-side VWO). */
  oncePerSession?: boolean;
}

export default function ExperimentTracker({
  experimentId,
  experimentName,
  variantName,
  cookieName,
  expectedValue,
  oncePerSession = false,
}: ExperimentTrackerProps): null {
  useEffect(() => {
    if (cookieName && readCookie(cookieName) !== expectedValue) return;
    const id = firedId(experimentId);
    if (oncePerSession && readFired().includes(id)) return;

    const report = (): boolean => {
      if (!window.analytics) return false;
      if (oncePerSession) markFired(id);
      segmentEvent("Experiment Viewed", {
        experiment_id: experimentId,
        experiment_name: experimentName,
        variant_name: variantName,
      });
      return true;
    };

    if (report()) return;
    let polls = 0;
    const timer = setInterval(() => {
      polls += 1;
      if (report() || polls >= SEGMENT_MAX_POLLS) clearInterval(timer);
    }, SEGMENT_POLL_MS);
    return () => clearInterval(timer);
  }, [experimentId, experimentName, variantName, cookieName, expectedValue, oncePerSession]);

  return null;
}
